import { createServiceHTTPClientProvider, createBoundServiceQueryHooksCreator, createBoundServiceMutationHookCreator } from "@airhelp/service-utils";
import { a as fetchOffers, c as fetchFeatureFlags, g as fetchLounges, a2 as fetchJourney, a4 as fetchJourneys, a7 as fetchCheckDeletableJourney, a9 as deleteJourney, ac as updateJourney, aj as addJourney, w as postPromoCode, am as cancelMembership, ap as fetchTrip, j as fetchAirPayoutJourneys, n as fetchAirLuggageJourneys, u as updateAirPayoutJourneys, p as updateAirLuggageJourneys, t as triggerAirPayoutJourneys, o as triggerAirLuggageJourneys, q as uploadAirBundleDocument, r as createVerificationSession, aq as fetchBillingManagement, au as updateBillingAddress, aw as updateCreditCard } from "./update-credit-card-De0-yYUE.js";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { P as ProductName } from "./enums-B6pzEDd7.js";
const [AhPlusHTTPClientProvider, useAhPlusHTTPClient] = createServiceHTTPClientProvider({
  name: "AhPlus"
});
const AH_PLUS_REACT_QUERY_KEY_PREFIX = "ah-plus";
const createAhPlusQueryHooks = createBoundServiceQueryHooksCreator({
  queryKeyPrefix: AH_PLUS_REACT_QUERY_KEY_PREFIX,
  useClient: useAhPlusHTTPClient
});
const createAhPlusMutationHook = createBoundServiceMutationHookCreator({
  useClient: useAhPlusHTTPClient
});
const [useOffers, useOffersSuspense] = createAhPlusQueryHooks({
  queryFn: fetchOffers,
  queryKey: ["offers"]
});
const [useFeatureFlags, useFeatureFlagsSuspense] = createAhPlusQueryHooks({
  queryFn: fetchFeatureFlags,
  queryKey: ["feature-flags"]
});
const [useLounges, useLoungesSuspense] = createAhPlusQueryHooks({
  queryFn: fetchLounges,
  queryKey: ["lounges"]
});
const [useJourney, useJourneySuspense] = createAhPlusQueryHooks({
  queryFn: fetchJourney,
  queryKey: ["journey"]
});
const [useJourneys, useJourneysSuspense] = createAhPlusQueryHooks({
  queryFn: fetchJourneys,
  queryKey: ["journeys"]
});
const [useCheckDeletableJourney, useCheckDeletableJourneySuspense] = createAhPlusQueryHooks({
  queryFn: fetchCheckDeletableJourney,
  queryKey: ["journey", "check-deletable-journey"]
});
const useInternalDeleteJourney = createAhPlusMutationHook({
  mutationFn: deleteJourney
});
function useDeleteJourney(options) {
  const queryClient = useQueryClient();
  return useInternalDeleteJourney({
    async onSuccess(data, params, context) {
      var _a;
      await queryClient.invalidateQueries({
        queryKey: useJourneys.getQueryKey()
      });
      queryClient.removeQueries({
        queryKey: useJourney.getQueryKey({
          params
        })
      });
      await ((_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context));
    }
  });
}
const useInternalUpdateJourney = createAhPlusMutationHook({
  mutationFn: updateJourney
});
function useUpdateJourney(options) {
  const queryClient = useQueryClient();
  return useInternalUpdateJourney({
    async onSuccess(data, params, context) {
      var _a;
      await queryClient.setQueryData(
        useJourney.getQueryKey({ params: { id: data.id } }),
        { ...data, computed: params.journey.computed }
      );
      await queryClient.invalidateQueries({
        queryKey: useJourneys.getQueryKey()
      });
      await ((_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context));
    }
  });
}
const useInternalAddJourney = createAhPlusMutationHook({
  mutationFn: addJourney
});
function useAddJourney(options) {
  const queryClient = useQueryClient();
  return useInternalAddJourney({
    async onSuccess(data, params, context) {
      var _a;
      await queryClient.invalidateQueries({
        queryKey: useJourneys.getQueryKey()
      });
      await ((_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context));
    }
  });
}
const useApplyPromoCode = createAhPlusMutationHook({
  mutationFn: postPromoCode
});
const useCancelMembership = createAhPlusMutationHook({
  mutationFn: cancelMembership
});
const [useTrip, useTripSuspense] = createAhPlusQueryHooks({
  queryFn: fetchTrip,
  queryKey: ["trip"]
});
const [useAirPayoutJourneys, useAirPayoutJourneysSuspense] = createAhPlusQueryHooks({
  queryFn: fetchAirPayoutJourneys,
  queryKey: ["journey", "air-payouts"]
});
const [useAirLuggageJourneys, useAirLuggageJourneysSuspense] = createAhPlusQueryHooks({
  queryFn: fetchAirLuggageJourneys,
  queryKey: ["journey", "air-luggages"]
});
const useInternalUpdateAirPayoutJourneys = createAhPlusMutationHook({
  mutationFn: updateAirPayoutJourneys
});
function useUpdateAirPayoutJourneys(options) {
  const queryClient = useQueryClient();
  return useInternalUpdateAirPayoutJourneys({
    async onSuccess(data, params, context) {
      var _a;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: useJourneys.getQueryKey()
        }),
        queryClient.invalidateQueries({
          queryKey: useAirPayoutJourneys.getQueryKey({
            params: { journeyId: params.journeyId }
          })
        }),
        queryClient.setQueryData(
          useJourney.getQueryKey({ params: { id: params.journeyId } }),
          data
        )
      ]);
      (_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context);
    }
  });
}
const useInternalUpdateAirLuggageJourneys = createAhPlusMutationHook({
  mutationFn: updateAirLuggageJourneys
});
function useUpdateAirLuggageJourneys(options) {
  const queryClient = useQueryClient();
  return useInternalUpdateAirLuggageJourneys({
    async onSuccess(data, params, context) {
      var _a;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: useJourneys.getQueryKey()
        }),
        queryClient.invalidateQueries({
          queryKey: useAirLuggageJourneys.getQueryKey({
            params: { journeyId: params.journeyId }
          })
        }),
        queryClient.setQueryData(
          useJourney.getQueryKey({ params: { id: params.journeyId } }),
          data
        )
      ]);
      (_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context);
    }
  });
}
const useInternalTriggerAirPayoutJourney = createAhPlusMutationHook({
  mutationFn: triggerAirPayoutJourneys
});
function useTriggerAirPayoutJourneys(options) {
  const queryClient = useQueryClient();
  return useInternalTriggerAirPayoutJourney({
    async onSuccess(data, params, context) {
      var _a;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: useJourney.getQueryKey({
            params: { id: params.journeyId }
          })
        }),
        queryClient.invalidateQueries({
          queryKey: useAirPayoutJourneys.getQueryKey({
            params: { journeyId: params.journeyId }
          })
        }),
        queryClient.setQueryData(
          useJourney.getQueryKey({ params: { id: params.journeyId } }),
          data
        )
      ]);
      (_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context);
    }
  });
}
const useInternalTriggerAirluggageJourney = createAhPlusMutationHook({
  mutationFn: triggerAirLuggageJourneys
});
function useTriggerAirLuggageJourneys(options) {
  const queryClient = useQueryClient();
  return useInternalTriggerAirluggageJourney({
    async onSuccess(data, params, context) {
      var _a;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: useJourney.getQueryKey({
            params: { id: params.journeyId }
          })
        }),
        queryClient.invalidateQueries({
          queryKey: useAirLuggageJourneys.getQueryKey({
            params: { journeyId: params.journeyId }
          })
        }),
        queryClient.setQueryData(
          useJourney.getQueryKey({ params: { id: params.journeyId } }),
          data
        )
      ]);
      (_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context);
    }
  });
}
const useUploadAirBundleDocument = createAhPlusMutationHook({
  mutationFn: uploadAirBundleDocument
});
const useCreateVerificationSession = createAhPlusMutationHook({
  mutationFn: createVerificationSession
});
const [useBillingManagement, useBillingManagementSuspense] = createAhPlusQueryHooks({
  queryFn: fetchBillingManagement,
  queryKey: ["billing-management"]
});
const useInternalUpdateBillingAddress = createAhPlusMutationHook({
  mutationFn: updateBillingAddress
});
function useUpdateBillingAddress(options) {
  const queryClient = useQueryClient();
  return useInternalUpdateBillingAddress({
    async onSuccess(data, params, context) {
      var _a;
      await queryClient.setQueryData(
        useBillingManagement.getQueryKey(),
        (current) => ({
          ...current,
          billingAddress: params.payload
        })
      );
      await queryClient.invalidateQueries({
        queryKey: useBillingManagement.getQueryKey()
      });
      await ((_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context));
    }
  });
}
const useUpdateCreditCard = createAhPlusMutationHook({
  mutationFn: updateCreditCard
});
const createPaymentIntentResponseSchema = z.object({
  clientSecret: z.string()
});
const createPaymentIntentSchema = z.object({
  email: z.string(),
  productName: z.nativeEnum(ProductName).or(z.string()),
  currency: z.string(),
  recurring: z.boolean(),
  channel: z.string(),
  locale: z.string(),
  tripUuid: z.string().optional(),
  upgrade: z.boolean().optional().default(false)
});
const orderSchema = z.object({
  currency: z.string(),
  discountAmount: z.any().pipe(z.coerce.number()).nullable().optional(),
  identifier: z.string(),
  paidAmount: z.any().pipe(z.coerce.number()).nullable().optional(),
  productName: z.nativeEnum(ProductName).or(z.string()),
  totalAmount: z.any().pipe(z.coerce.number()).nullable().optional()
});
const paymentConfirmationResponseSchema = z.object({
  order: orderSchema
});
const getPaymentConfirmationSchema = z.object({
  id: z.string()
});
const paymentStatusResponseSchema = z.object({
  setupIntentId: z.string(),
  status: z.string(),
  errorCode: z.string().nullable().optional()
});
const getPaymentStatusSchema = z.object({
  id: z.string()
});
const updatePaymentIntentResponseSchema = z.object({
  currency: z.string(),
  discountValue: z.any().pipe(z.coerce.number()).nullable().optional(),
  discountedPrice: z.any().pipe(z.coerce.number()).nullable().optional(),
  productPrice: z.any().pipe(z.coerce.number()).nullable().optional()
});
const updatePaymentIntentSchema = z.object({
  currency: z.string(),
  upgrade: z.boolean().optional().default(false),
  id: z.string()
});
function createStripeCheckout({
  client,
  params
}) {
  const payload = createPaymentIntentSchema.parse(params);
  return client.url(`/api/v2/stripe_checkouts`).snakeCaseBody(payload).post().valid(createPaymentIntentResponseSchema);
}
const useCreatePayment = createAhPlusMutationHook({
  mutationFn: createStripeCheckout
});
function getPaymentStatus({
  client,
  params
}) {
  const { id } = getPaymentStatusSchema.parse(params);
  return client.get(`/api/v2/stripe_checkouts/${id}`).valid(paymentStatusResponseSchema);
}
const [usePaymentStatus, usePaymentStatusSuspense] = createAhPlusQueryHooks({
  queryFn: getPaymentStatus,
  queryKey: ["payment-status"]
});
function updateStripeCheckout({
  client,
  params
}) {
  const { id, ...rest } = params;
  const payload = updatePaymentIntentSchema.parse(rest);
  return client.url(`/api/v2/stripe_checkouts/${id}`).snakeCaseBody(payload).patch().valid(updatePaymentIntentResponseSchema);
}
const useUpdatePaymentIntent = createAhPlusMutationHook({
  mutationFn: updateStripeCheckout
});
function getPaymentConfirmation({
  client,
  params
}) {
  const { id } = getPaymentConfirmationSchema.parse(params);
  return client.get(`/api/v2/payments_confirmations/${id}`).valid(paymentConfirmationResponseSchema);
}
const [usePaymentConfirmation, usePaymentConfirmationSuspense] = createAhPlusQueryHooks({
  queryFn: getPaymentConfirmation,
  queryKey: ["payment-confirmation"]
});
export {
  AH_PLUS_REACT_QUERY_KEY_PREFIX,
  AhPlusHTTPClientProvider,
  useAddJourney,
  useAhPlusHTTPClient,
  useAirLuggageJourneys,
  useAirLuggageJourneysSuspense,
  useAirPayoutJourneys,
  useAirPayoutJourneysSuspense,
  useApplyPromoCode,
  useBillingManagement,
  useBillingManagementSuspense,
  useCancelMembership,
  useCheckDeletableJourney,
  useCheckDeletableJourneySuspense,
  useCreatePayment,
  useCreateVerificationSession,
  useDeleteJourney,
  useFeatureFlags,
  useFeatureFlagsSuspense,
  useJourney,
  useJourneySuspense,
  useJourneys,
  useJourneysSuspense,
  useLounges,
  useLoungesSuspense,
  useOffers,
  useOffersSuspense,
  usePaymentConfirmation,
  usePaymentConfirmationSuspense,
  usePaymentStatus,
  usePaymentStatusSuspense,
  useTriggerAirLuggageJourneys,
  useTriggerAirPayoutJourneys,
  useTrip,
  useTripSuspense,
  useUpdateAirLuggageJourneys,
  useUpdateAirPayoutJourneys,
  useUpdateBillingAddress,
  useUpdateCreditCard,
  useUpdateJourney,
  useUpdatePaymentIntent,
  useUploadAirBundleDocument
};
//# sourceMappingURL=react.js.map
